import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Subscription } from 'rxjs/internal/Subscription';

import { YeehroCalendlyBooking } from '@app/models';
import { SsModuleService } from '@app/services';

// Meeting reminder pop-up (case 1504). Display only: MeetingReminderService owns the rows and
// pushes updates through data.meetings$. Must not inject MeetingReminderService (it opens this dialog).
@Component({
    selector: 'app-meeting-reminder-dialog',
    templateUrl: './meeting-reminder-dialog.component.html',
    styleUrls: ['./meeting-reminder-dialog.component.scss'],
    standalone: false
})
export class MeetingReminderDialogComponent implements OnInit, OnDestroy {
  meetings: YeehroCalendlyBooking[] = [];
  now: number = Date.now();

  private meetingsSub: Subscription = null;
  private tickTimer: any = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { meetings$: BehaviorSubject<YeehroCalendlyBooking[]> },
    private _dialogRef: MatDialogRef<MeetingReminderDialogComponent>,
    private _moduleService: SsModuleService,
    private _router: Router
  ) {}


  ngOnInit() {
    this.meetingsSub = this.data?.meetings$?.subscribe(meetings => this.meetings = meetings || []);
    this.tickTimer = setInterval(() => this.now = Date.now(), 1000); // drives the live countdown, only while open
  }


  ngOnDestroy() {
    this.meetingsSub?.unsubscribe();
    if (this.tickTimer) clearInterval(this.tickTimer);
  }


  // "Starts in 7 min", seconds in the final minute, then "Starting now" / "Started 2 min ago".
  getCountdownLabel(meeting: YeehroCalendlyBooking): string {
    const diffMs = new Date(meeting?.start).getTime() - this.now;
    if (diffMs >= 60000) return `Starts in ${Math.ceil(diffMs / 60000)} min`;
    if (diffMs > 0) return `Starts in ${Math.ceil(diffMs / 1000)}s`;

    const minutesAgo = Math.floor(-diffMs / 60000);
    return minutesAgo < 1 ? 'Starting now' : `Started ${minutesAgo} min ago`;
  }


  hasStarted(meeting: YeehroCalendlyBooking): boolean {
    return new Date(meeting?.start).getTime() <= this.now;
  }


  getInviteeNames(meeting: YeehroCalendlyBooking): string {
    return (meeting?.invitees || []).map(i => i?.name).filter(Boolean).join(', ');
  }


  getEventColor(meeting: YeehroCalendlyBooking): string {
    return meeting?.event?.color?.primary || '#8247F5';
  }


  hasRelatedRecord(meeting: YeehroCalendlyBooking): boolean {
    return !!(meeting?.relatedRecord?.recordType && meeting?.relatedRecord?.recordId);
  }


  // "View Lead", "View Sub", etc. Same helper the Meetings page row uses.
  getRelatedRecordLinkLabel(meeting: YeehroCalendlyBooking): string {
    const singular = this._moduleService?.getSingularModuleLabel(meeting?.relatedRecord?.recordType);
    return singular ? `View ${singular}` : 'View record';
  }


  viewRelatedRecord(meeting: YeehroCalendlyBooking) {
    const baseRoute = this._moduleService?.getBaseRouteForModule(meeting?.relatedRecord?.recordType);
    if (!baseRoute) return;
    this._dialogRef.close();
    this._router?.navigate([baseRoute + '/' + (meeting?.relatedRecord?.recordId?._id || meeting?.relatedRecord?.recordId)]);
  }


  goToMeetings() {
    this._dialogRef.close();
    this._router?.navigate(['/app/booking-admin/meetings']);
  }


  close() {
    this._dialogRef.close();
  }
}
